import React, { useState, useEffect } from 'react';
import { Sparkles, Zap, Heart, Gem, Clock, ChevronRight, Loader2 } from 'lucide-react';
import { getServices, groupServicesByCategory } from '../services/api';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

const iconMap = {
  'Depilação': Sparkles,
  'Lipozero': Zap,
  'Pressoterapia': Heart,
  'Sessões de Beleza': Gem,
};

const Services = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchServices = async () => {
      const data = await getServices();
      const grouped = groupServicesByCategory(data);
      setServices(grouped);
      setLoading(false);
    };
    fetchServices();
  }, []);

  if (loading) {
    return (
      <section id="servicos" className="py-24 bg-[#0a0a0a] relative flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 text-[#E1C57C] animate-spin" />
      </section>
    );
  }

  return (
    <section id="servicos" className="py-24 bg-[#0a0a0a] relative">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(229,177,196,0.05)_0%,_transparent_50%)]" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-[#E5B1C4] text-sm tracking-[0.3em] uppercase mb-4 block">
            Os Nossos Serviços
          </span>
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl text-gold-gradient mb-6">
            Tratamentos Exclusivos
          </h2>
          <div className="flex items-center justify-center mb-6">
            <div className="w-24 h-px bg-gradient-to-r from-transparent to-[#C1A54F]" />
            <span className="mx-4 text-[#E1C57C]">✦</span>
            <div className="w-24 h-px bg-gradient-to-l from-transparent to-[#C1A54F]" />
          </div>
          <p className="text-[#bbb5ae] max-w-2xl mx-auto text-lg">
            Tecnologia de ponta e produtos premium para realçar a sua beleza natural
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {services.map((category, catIndex) => {
            const IconComponent = iconMap[category.category] || Sparkles;
            return (
              <div key={catIndex} className="service-card p-8 rounded-sm">
                {/* Category Header */}
                <div className="flex items-center mb-6">
                  <div className="w-14 h-14 rounded-full border border-[#C1A54F]/30 flex items-center justify-center mr-4 bg-[#C1A54F]/5">
                    <IconComponent className="text-[#E1C57C]" size={24} />
                  </div>
                  <div>
                    <h3 className="font-display text-2xl text-[#f5f0e8]">
                      {category.category}
                    </h3>
                    {category.category === 'Lipozero' && (
                      <p className="text-[#bbb5ae] text-sm mt-1">
                        Laser Frio, RF, Cavitação, Vácuo e LED
                      </p>
                    )}
                  </div>
                </div>

                {/* Services List */}
                <Accordion type="single" collapsible className="space-y-2">
                  {category.items.map((item, index) => (
                    <AccordionItem
                      key={item.id || index}
                      value={`item-${catIndex}-${index}`}
                      className="border-[#C1A54F]/10 border-b"
                    >
                      <AccordionTrigger className="text-[#f5f0e8] hover:text-[#E1C57C] py-4 text-left">
                        <span className="text-sm">{item.name}</span>
                      </AccordionTrigger>
                      <AccordionContent className="pb-4">
                        <div className="flex justify-between items-center text-sm">
                          <div className="flex items-center text-[#bbb5ae]">
                            <Clock size={14} className="mr-2" />
                            {item.duration}
                          </div>
                          <span className="text-[#E1C57C] font-medium">
                            {item.price}
                          </span>
                        </div>
                        {item.description && (
                          <p className="text-[#bbb5ae] text-xs mt-2">{item.description}</p>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>

                {/* Book Now Link */}
                <a
                  href="#marcacoes"
                  className="mt-6 inline-flex items-center text-[#E5B1C4] hover:text-[#FADCD9] text-sm transition-colors group"
                >
                  Marcar {category.category}
                  <ChevronRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            );
          })}
        </div>

        {/* Lipozero Info Box */}
        <div className="mt-12 p-8 border border-[#C1A54F]/20 rounded-sm bg-gradient-to-r from-[#C1A54F]/5 to-transparent">
          <h3 className="font-display text-2xl text-[#E1C57C] mb-4">O que é o Lipozero?</h3>
          <p className="text-[#bbb5ae] leading-relaxed mb-6">
            O Lipozero é um tratamento inovador que combina 5 tecnologias num só equipamento para resultados excecionais:
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
            {['Laser Frio', 'Radiofrequência', 'Cavitação', 'Vácuo', 'LED'].map((tech, index) => (
              <div key={index} className="text-center p-4 bg-[#0a0a0a] rounded-sm border border-[#C1A54F]/10">
                <span className="text-[#E1C57C] text-xs block mb-1">✦</span>
                <span className="text-[#f5f0e8] text-sm">{tech}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
